import axios from "axios"
import jwtDecode from "jwt-decode"

//const BASE_URL = "http://localhost:8000/api/"
const BASE_URL = "/api/"
const OBTAIN_JWT = BASE_URL + "auth/obtain/"
const REFRESH_JWT = BASE_URL + "auth/refresh/"

class InternalClient {
  constructor(loginHandler) {
    this.loginHandler = loginHandler
    this.tokenCall = null

    this.client = axios.create({
      baseURL: BASE_URL,
      // TODO do we need the next two?
      xsrfCookieName: "csrftoken",
      xsrfHeaderName: "X-CSRFToken",
      headers: {
        "Content-Type": "application/json"
      },
      xhrFields: {
        // TODO: not needed in prod
        withCredentials: true,
      }
    })

    this.client.interceptors.request.use(config => {
      return this.getToken().then(token => {
        config.headers.Authorization = `Bearer ${token}`
        return config
      })
    }, error => Promise.reject(error))

    this.client.interceptors.response.use(response => Promise.resolve(response.data), error => {
      const status = error.response ? error.response.status : null

      if (status === 401) {
        window.localStorge.removeItem("access")
        this.getToken()
        return Promise.resolve(axios(error.config))
      }

      return Promise.reject(error)
    })
  }

  getToken() {
    if (this.tokenCall) return this.tokenCall
    const token = window.localStorage.getItem("access")
    if (!token || jwtDecode(token).exp < new Date().getTime() / 1000 + 10)
      return this.refreshJWT()
    return Promise.resolve(token)
  }

  refreshJWT() {
    return this.tokenCall = new Promise((resolve, reject) => {
      const refresh = window.localStorage.getItem("refresh")
      if (!refresh || jwtDecode(refresh).exp < new Date().getTime() / 1000 + 10) {
        this.obtainJWT().then(resolve, reject)
      } else {
        axios.post(REFRESH_JWT, {refresh})
          .then(response => {
            const access = response.data.access
            window.localStorage.setItem("access", access)
            this.tokenCall = null
            resolve(access)
          })
          .catch(error => {
            window.localStorage.removeItem("refresh")
            this.obtainJWT().then(resolve, reject)
          })
      }
    })
  }

  obtainJWT() {
    return new Promise((resolve, reject) => {
      this.loginHandler().then(({refresh, access}) => {
        window.localStorage.setItem("refresh", refresh)
        window.localStorage.setItem("access", access)
        this.tokenCall = null
        resolve(access)
      })
    })
  }

  login(username, password) {
    return axios.post(OBTAIN_JWT, {username, password})
      .then(response => response.data)
  }

  logout() {
    window.localStorage.removeItem("refresh")
    window.localStorage.removeItem("access")
    return Promise.resolve()
  }

  changePassword(oldpass, newpass, confirmpass) {
    return this.client.post("auth/change/", {
      oldpass,
      newpass,
      confirmpass
    })
  }

  get(url, params) {
    return this.client.get(url, {params})
  }

  post(url, data) {
    return this.client.post(url, data)
  }
}

export default class HttpClient {
  constructor(loginHandler, progressHandler) {
    this.progressHandler = progressHandler
    this.client = new InternalClient(loginHandler)
  }

  login(username, password) {
    return this.client.login(username, password)
  }

  logout() {
    return this.client.logout()
  }

  changePassword(oldpass, newpass, confirmpass) {
    this.client.changePassword(oldpass, newpass, confirmpass)
  }

  _loadPaginated({api, page=1, query={}}) {
    return this.client.get(api, {...query, page})
  }

  loadFilms(input) {
    return this._loadPaginated({...input, api: "films/"})
  }

  loadRatings(input) {
    return this._loadPaginated({...input, api: "ratings/"})
  }

  loadReviews(input) {
    return this._loadPaginated({...input, api: "reviews/"})
  }

  loadLists(input) {
    return this._loadPaginated({...input, api: "lists/"})
  }

  async _loadAll({api, callback, query={}}) {
    const items = []
    var page = 1
    var data 
    this.progressHandler.start()
    do {
      data = await this.client.get(api, {...query, page, page_size: 100})
      if (callback) await callback(data.results)
      items.splice(items.length, 0, ...data.results)
      this.progressHandler.progress(items.length, data.count)
      page++
    } while (data.has_next)
    this.progressHandler.end()
    return items
  }

  loadAllLists(query={}) {
    return this._loadAll({query, api: "lists/"})
  }

  loadAllFilms({query, callback}) {
    return this._loadAll({query, callback, api: "films/"})
  }

  addFilm({id}) {
    return this.client.post("films/", {
      imdb_id: id
    })
  }

  loadFilm({id}) {
    return this.client.get(`films/${id}/`, {})
  }

  loadList({id}) {
    return this.client.get(`lists/${id}/`, {})
  }

  loadUsers() {
    return this.client.get("users/rest/", {})
  }

  loadMe() {
    return this.client.get("users/me/", {})
  }

  loadAllUsers() {
    return this.client.get("users/all/", {})
  }

  loadNotifications() {
    return this.client.get("notifications/", {})
  }

  markNotificationAsRead({id}) {
    return this.client.post(`notifications/${id}/read/`, {})
  }

  loadCategories() {
    return this.client.get("categories/", {})
  }

  loadAllTags() {
    return this.client.get("tags/", {})
  }

  loadTags({category}) {
    return this.client.get(`categories/${category}/`, {})
  }

  addTag({id, category, tag}) {
    return this.client.post("tags/create/", {
      film_id: id,
      category,
      tag
    })
  }

  removeTag({id, category, tag}) {
    return this.client.post("tags/remove/", {
      film_id: id,
      category,
      tag
    })
  }

  editTag({id, category, old_tag, new_tag}) {
    return this.client.post("tags/edit/", {
      film_id: id,
      category,
      old_tag,
      new_tag
    })
  }

  toggleOwn(id) {
    return this.client.post(`films/${id}/own/`)
  }

  rateFilm({id, rating}) {
    return this.client.post(`films/${id}/rate/`, {rating})
  }

  updateReview({id, review}) {
    return this.client.post(`films/${id}/review/`, {review})
  }

  suggestFilm({id, user_id}) {
    return this.client.post(`films/${id}/suggest/`, {user_id})
  }

  addList({title, description}) {
    return this.client.post("lists/", {title, description})
  }

  closeList({id}) {
    return this.client.post(`/lists/${id}/close/`, {})
  }

  openList({id}) {
    return this.client.post(`/lists/${id}/open/`, {})
  }

  addToList({id, film_id}) {
    return this.client.post(`/lists/${id}/add/`, {id: film_id})
  }

  removeFromList({id, film_id}) {
    return this.client.post(`/lists/${id}/remove/`, {id: film_id})
  }

  searchPersons({query}) {
    return this.client.get(`persons/`, {query})
  }
}
